/**
 * Minimal GraphQL literal serializer.
 *
 * The artifact list query is built dynamically per artifact type, so we inline
 * ``where`` / ``orderBy`` argument values directly into the query string rather
 * than declaring typed operation variables (whose input type names vary per
 * type and aren't published in the UI-config document). This keeps the query
 * builder fully generic.
 *
 * Serialization follows GraphQL value syntax:
 * - object keys are bare names (not quoted, unlike JSON)
 * - strings use JSON escaping (compatible with GraphQL string literals)
 * - enum values are emitted as bare identifiers via {@link gqlEnum}
 */

/** GraphQL Name: https://spec.graphql.org/October2021/#Name */
const GRAPHQL_NAME = /^[_A-Za-z][_0-9A-Za-z]*$/;

/** True when `name` can be emitted as a bare GraphQL key or field. */
export function isGraphQLName(name: string): boolean {
  return GRAPHQL_NAME.test(name);
}

/** Marker wrapper for a value that must serialize as a GraphQL enum token. */
export interface GraphQLEnum {
  readonly __gqlEnum: string;
}

export function gqlEnum(token: string): GraphQLEnum {
  return { __gqlEnum: token };
}

function isEnum(value: unknown): value is GraphQLEnum {
  return typeof value === 'object' && value !== null && '__gqlEnum' in value;
}

/** Serialize a JS value into a GraphQL literal string. */
export function toGraphQLLiteral(value: unknown): string {
  if (value === null || value === undefined) return 'null';
  if (isEnum(value)) return value.__gqlEnum;
  if (typeof value === 'string') return JSON.stringify(value);
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    return `[${value.map(toGraphQLLiteral).join(', ')}]`;
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>).filter(
      ([k, v]) => v !== undefined && isGraphQLName(k),
    );
    if (entries.length === 0) return '{}';
    return `{ ${entries.map(([k, v]) => `${k}: ${toGraphQLLiteral(v)}`).join(', ')} }`;
  }
  return 'null';
}
